import { useMemo } from "react";
import { ArrowLeft, Search } from "lucide-react";
import { AdCard } from "@/app/components/ad-card";
import { BottomNav } from "@/app/components/bottom-nav";
import { CommonHeader } from "@/app/components/common-header";
import { CategorySearchFilters } from "@/app/components/category-search-filters";
import { Button } from "@/app/components/button";
import { Footer, type PopularCategory } from "@/app/components/footer";
import type { Ad } from "@/app/components/ad-card";
import type { Category } from "@/app/api/auth-client";
import { buildCategoryPathLabel, isOpaqueCatalogCategoryId } from "@/app/utils/category-tree";
import { useAccountNavEnabled } from "@/app/context/account-nav-context";

interface CategoryResultsPageProps {
  category: string;
  /** When set, used for page title instead of selectedCategory (e.g. "Instrumentos Musicais" instead of cat_21d5fca52759). */
  categoryDisplayName?: string;
  /** Flat catalog from GET /catalog/categories; used to resolve labels when categoryDisplayName is still a cat_* id. */
  categoryCatalog?: Category[] | null;
  ads: Ad[];
  /** When true, ads are from server search (GET /ads/search); only custom filters + sort applied client-side; show load more if hasMore. */
  useServerSearch?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  searchResultsLoading?: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  categories: string[];
  popularCategories?: PopularCategory[];
  currentLocation?: string;
  onLocationClick?: () => void;
  onSortClick?: () => void;
  onCustomFilterClick?: () => void;
  activeFiltersCount?: number;
  currentSort: string;
  customFilters: {
    brands: string[];
    conditions: string[];
    priceMin: string;
    priceMax: string;
    locations: string[];
  };
  isFavorite: (adId: string) => boolean;
  onToggleFavorite: (adId: string) => void;
  onAdClick: (adId: string) => void;
  onBackToHome?: () => void;
  /** Logo / footer brand → home (defaults to onBackToHome when omitted) */
  onLogoClick?: () => void;
  cartCount: number;
  notificationCount: number;
  onCartClick: () => void;
  onNotificationsClick: () => void;
  onAddressClick: () => void;
  activeTab: "home" | "favorites";
  onTabChange: (tab: "home" | "favorites") => void;
  favoritesCount: number;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
  onMenuClick: () => void;
  onNavigateToAbout?: () => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacyPolicy?: () => void;
  onNavigateToHelp?: () => void;
  headerUserName?: string;
  headerAddress?: string;
  isLoggedIn?: boolean;
  onLoginClick?: () => void;
  onSignUpClick?: () => void;
  onFavoritesClick?: () => void;
  onMyAdsClick?: () => void;
  onWalletClick?: () => void;
}

export function CategoryResultsPage({
  category,
  categoryDisplayName,
  categoryCatalog = null,
  ads,
  useServerSearch = false,
  onLoadMore,
  hasMore = false,
  searchResultsLoading = false,
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  categories: _categories,
  popularCategories = [],
  currentLocation,
  onLocationClick,
  onSortClick,
  onCustomFilterClick,
  activeFiltersCount,
  currentSort,
  customFilters,
  isFavorite,
  onToggleFavorite,
  onAdClick,
  onBackToHome,
  onLogoClick,
  cartCount,
  notificationCount,
  onCartClick,
  onNotificationsClick,
  onAddressClick,
  activeTab,
  onTabChange,
  favoritesCount,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
  onMenuClick,
  onNavigateToAbout,
  onNavigateToTerms,
  onNavigateToPrivacyPolicy,
  onNavigateToHelp,
  headerUserName = "",
  headerAddress = "",
  isLoggedIn = true,
  onLoginClick,
  onSignUpClick,
  onFavoritesClick,
  onMyAdsClick,
  onWalletClick,
}: CategoryResultsPageProps) {
  const accountNav = useAccountNavEnabled();

  /** Never show raw cat_* in copy; prefer display name, path from catalog, or neutral phrase. */
  const humanReadableCategoryLabel = useMemo(() => {
    if (selectedCategory === "Todos") return "Todos os Produtos";
    const fromParent = categoryDisplayName?.trim();
    if (fromParent && !isOpaqueCatalogCategoryId(fromParent)) return fromParent;
    const cats = categoryCatalog ?? [];
    if (cats.length > 0) {
      const path = buildCategoryPathLabel(cats, selectedCategory);
      if (path) return path;
      const direct = cats.find((c) => c.category_id.trim() === selectedCategory.trim())?.name?.trim();
      if (direct) return direct;
    }
    if (isOpaqueCatalogCategoryId(selectedCategory)) return "esta categoria";
    return selectedCategory;
  }, [categoryDisplayName, categoryCatalog, selectedCategory]);

  // When useServerSearch, BFF/Sambora already filter by category; only custom filters + sort apply client-side.
  const filteredAds = ads.filter((ad) => {
    if (!useServerSearch) {
      const matchesSearch = ad.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            ad.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === "Todos" || ad.category === selectedCategory;
      if (!matchesSearch || !matchesCategory) return false;
    }
    const matchesBrand = customFilters.brands.length === 0 || customFilters.brands.includes(ad.brand || "");
    const matchesCondition = customFilters.conditions.length === 0 || customFilters.conditions.includes(ad.condition || "");
    const matchesPriceMin = !customFilters.priceMin || ad.price >= parseFloat(customFilters.priceMin);
    const matchesPriceMax = !customFilters.priceMax || ad.price <= parseFloat(customFilters.priceMax);
    const matchesLocation = customFilters.locations.length === 0 || customFilters.locations.includes(ad.location);
    return matchesBrand && matchesCondition && matchesPriceMin && matchesPriceMax && matchesLocation;
  });

  // Sort filtered ads
  const sortedAds = [...filteredAds].sort((a, b) => {
    switch (currentSort) {
      case "newest":
        return parseInt(b.id) - parseInt(a.id);
      case "oldest":
        return parseInt(a.id) - parseInt(b.id);
      case "price-low":
        return a.price - b.price;
      case "price-high":
        return b.price - a.price;
      case "brand-az":
        return (a.brand || "").localeCompare(b.brand || "");
      default:
        return 0;
    }
  });

  // Generate dynamic header based on search state
  const getPageTitle = () => {
    if (searchQuery.trim() !== "") {
      return "Resultados da Busca";
    }
    if (selectedCategory !== "Todos") {
      return humanReadableCategoryLabel;
    }
    return "Todos os Produtos";
  };

  const getPageSubtitle = () => {
    if (searchQuery.trim() !== "") {
      return `Buscando por "${searchQuery}"`;
    }
    return "Navegue pelos produtos disponíveis";
  };

  const goHomeFromLogo = onLogoClick ?? onBackToHome;

  return (
    <div className="min-h-screen bg-background pb-20 lg:pb-0">
      {/* Header */}
      <CommonHeader
        isLoggedIn={isLoggedIn}
        userName={headerUserName}
        currentAddress={headerAddress}
        onLoginClick={onLoginClick}
        onSignUpClick={onSignUpClick}
        cartCount={cartCount}
        notificationCount={notificationCount}
        favoritesCount={favoritesCount}
        onCartClick={onCartClick}
        onNotificationsClick={onNotificationsClick}
        onAddressClick={onAddressClick}
        onMenuClick={onMenuClick}
        onFavoritesClick={onFavoritesClick}
        onMyAdsClick={onMyAdsClick}
        onWalletClick={onWalletClick}
        onLogoClick={goHomeFromLogo}
      />

      {/* Category Header with Back Button */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            {onBackToHome && (
              <button
                onClick={onBackToHome}
                className="p-2 hover:bg-accent rounded-lg transition-colors"
                aria-label="Voltar para Home"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
            )}
            <div>
              <h1 className="text-2xl font-semibold">{getPageTitle()}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {getPageSubtitle()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <CategorySearchFilters
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        currentLocation={currentLocation}
        onLocationClick={onLocationClick}
        onSortClick={onSortClick}
        onCustomFilterClick={onCustomFilterClick}
        activeFiltersCount={activeFiltersCount}
      />

      {/* Main Content */}
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 pb-24 lg:pb-8">
        {useServerSearch && searchResultsLoading && ads.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">Carregando resultados...</p>
          </div>
        ) : sortedAds.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-10 h-10 text-secondary-foreground" />
            </div>
            <h3 className="mb-2">Nenhum item encontrado</h3>
            <p className="text-muted-foreground mb-6">
              Tente ajustar sua busca ou filtros para {humanReadableCategoryLabel}
            </p>
            <Button
              onClick={() => {
                onSearchChange("");
                onCategoryChange("Todos");
              }}
            >
              Limpar Filtros
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-muted-foreground">
                {sortedAds.length} {sortedAds.length === 1 ? "item disponível" : "itens disponíveis"}
                {selectedCategory !== "Todos" && selectedCategory !== category && (
                  <span className="ml-2 text-primary font-medium">
                    · {humanReadableCategoryLabel}
                  </span>
                )}
              </p>
              {selectedCategory !== category && (
                <button
                  onClick={() => onCategoryChange(category)}
                  className="text-sm text-primary hover:underline"
                >
                  Resetar para {humanReadableCategoryLabel}
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {sortedAds.map((ad) => (
                <AdCard 
                  key={ad.id} 
                  ad={ad} 
                  isFavorite={isFavorite(ad.id)}
                  onToggleFavorite={accountNav ? onToggleFavorite : undefined}
                  onAdClick={() => onAdClick(ad.id)}
                />
              ))}
            </div>
            {useServerSearch && hasMore && (
              <div className="mt-8 flex justify-center">
                <Button
                  onClick={onLoadMore}
                  disabled={searchResultsLoading}
                  variant="outline"
                >
                  {searchResultsLoading ? "Carregando..." : "Carregar mais"}
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={(tab) => onTabChange(tab as "home" | "favorites")}
        favoritesCount={favoritesCount}
        onPostAdClick={onPostAdClick}
        onMenuClick={onMenuClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />

      {/* Footer */}
      <Footer
        onLogoClick={goHomeFromLogo}
        onNavigateToAbout={onNavigateToAbout}
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacyPolicy={onNavigateToPrivacyPolicy}
        onNavigateToHelp={onNavigateToHelp}
        popularCategories={popularCategories}
        onCategoryClick={onCategoryChange}
      />
    </div>
  );
}